import AdminNav from '@/components/admin-nav';
import { admin_project_path, admin_projects_path } from '@/rails/routes';
import type { AdminProjectsIndex, WorkspacePageProps } from '@/types';
import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Icon } from '@astryxdesign/core/Icon';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';
import { VStack } from '@astryxdesign/core/VStack';
import { Form, router, usePage } from '@inertiajs/react';
import { FolderKanban, Palette, Plus, Power, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useDirtyGuard } from '@/components/dirty-guard';

type Props = WorkspacePageProps & AdminProjectsIndex;

const DEFAULT_PROJECT_COLOR = '#d84b42';

const Index = () => {
  const { projects } = usePage<Props>().props;
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectColor, setNewProjectColor] = useState(DEFAULT_PROJECT_COLOR);
  // Avisar antes de salir con un proyecto nuevo en curso.
  useDirtyGuard(newProjectName !== '');

  const toggleActive = (id: string, active: boolean) => {
    if (active) {
      router.delete(admin_project_path(id), { preserveScroll: true });
    } else {
      router.patch(admin_project_path(id), { active: true }, { preserveScroll: true });
    }
  };

  const updateColor = (id: string, color: string) => {
    router.patch(admin_project_path(id), { color }, { preserveScroll: true });
  };

  return (
    <VStack gap={6} width="100%" className="iorio-projects-page">
      <AdminNav />

      <section className="iorio-projects-grid" aria-label="Administración de proyectos">
        <Card padding={6} className="iorio-projects-card iorio-projects-list-card">
          <VStack gap={5}>
            <HStack hAlign="between" vAlign="start" gap={3} wrap="wrap">
              <VStack gap={1}>
                <HStack gap={2} vAlign="center">
                  <span className="iorio-projects-icon" aria-hidden="true">
                    <Icon icon={FolderKanban} size="sm" />
                  </span>
                  <Heading level={2}>Proyectos disponibles</Heading>
                </HStack>
                <Text type="supporting" color="secondary">
                  Desactivar un proyecto lo saca del selector sin afectar reportes guardados.
                </Text>
              </VStack>
              <Badge label={`${projects.length} totales`} variant="red" />
            </HStack>

            {projects.length === 0 ? (
              <VStack gap={2} hAlign="center" className="iorio-projects-empty">
                <span className="iorio-projects-icon" aria-hidden="true">
                  <Icon icon={FolderKanban} size="sm" />
                </span>
                <Text type="body" weight="bold">
                  Todavía no hay proyectos
                </Text>
                <Text type="supporting" color="secondary">
                  Creá el primero para que el equipo pueda usarlo en sus partes diarios.
                </Text>
              </VStack>
            ) : (
              <ul className="iorio-projects-list">
                {projects.map((project) => (
                  <li key={project.id} className="iorio-projects-row">
                    <HStack hAlign="between" vAlign="center" gap={3} wrap="wrap">
                      <HStack gap={3} vAlign="center" className="iorio-projects-row-main">
                        <input
                          type="color"
                          value={project.color}
                          aria-label={`Color de ${project.name}`}
                          className="iorio-projects-swatch"
                          onChange={(event) => updateColor(project.id, event.currentTarget.value)}
                        />
                        <Text type="body" weight="bold" className="iorio-projects-name">
                          {project.name}
                        </Text>
                        {!project.active && <Badge label="Inactivo" variant="neutral" />}
                      </HStack>

                      <Button
                        label={project.active ? 'Desactivar' : 'Reactivar'}
                        size="sm"
                        variant={project.active ? 'ghost' : 'secondary'}
                        icon={project.active ? <Power size={15} /> : <RotateCcw size={15} />}
                        onClick={() => toggleActive(project.id, project.active)}
                      />
                    </HStack>
                  </li>
                ))}
              </ul>
            )}
          </VStack>
        </Card>

        <Card padding={6} className="iorio-projects-card iorio-projects-form-card">
          <Form
            method="post"
            action={admin_projects_path()}
            resetOnSuccess
            onSuccess={() => {
              setNewProjectName('');
              setNewProjectColor(DEFAULT_PROJECT_COLOR);
            }}
          >
            {({ processing }) => (
              <VStack gap={5}>
                <VStack gap={2}>
                  <HStack gap={2} vAlign="center">
                    <span className="iorio-projects-icon" aria-hidden="true">
                      <Icon icon={Plus} size="sm" />
                    </span>
                    <Heading level={2}>Nuevo proyecto</Heading>
                  </HStack>
                  <Text type="supporting" color="secondary">
                    Elegí un nombre claro y un color fácil de reconocer en el parte diario.
                  </Text>
                </VStack>

                <VStack gap={4}>
                  <TextInput
                    label="Nombre"
                    htmlName="name"
                    placeholder="Nombre del proyecto"
                    value={newProjectName}
                    onChange={setNewProjectName}
                    size="lg"
                    startIcon={FolderKanban}
                    isRequired
                  />

                  <label className="iorio-projects-color-field">
                    <HStack gap={2} vAlign="center">
                      <Icon icon={Palette} size="sm" />
                      <Text type="supporting" weight="bold">
                        Color
                      </Text>
                    </HStack>
                    <HStack gap={3} vAlign="center">
                      <input
                        type="color"
                        name="color"
                        value={newProjectColor}
                        onChange={(event) => setNewProjectColor(event.currentTarget.value)}
                        className="iorio-projects-color-input"
                        aria-label="Color del proyecto"
                      />
                      <Text type="supporting" color="secondary">
                        Se usa como marca visual del proyecto en reportes y listados.
                      </Text>
                    </HStack>
                  </label>
                </VStack>

                <Button
                  label="Crear proyecto"
                  variant="primary"
                  type="submit"
                  size="lg"
                  icon={<Plus size={18} />}
                  isLoading={processing}
                />
              </VStack>
            )}
          </Form>
        </Card>
      </section>
    </VStack>
  );
};

export default Index;
